import { Trans } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ClipboardList, CreditCard, Mail, Stethoscope } from "lucide-react";

import { PageHeader } from "@/components/layout/page-header";

import {
  NotificationSettingsClient,
  type NotificationCategory,
} from "@/components/account/notification-settings-client";

/**
 * Facility-scoped notification preferences. Renders the per-category ×
 * per-channel matrix for the categories that matter to a facility admin
 * (staff invites, leave requests, provider invites, payments, capacity
 * alerts). User-only features (reminder timing + web-push subscription)
 * are hidden because they live on the per-user account, not on a facility.
 */
export const Route = createFileRoute(
  "/_authenticated/account/facility/$facilityId/settings/notifications",
)({
  component: FacilityNotificationSettingsPage,
});

const FACILITY_CATEGORIES: ReadonlyArray<NotificationCategory> = [
  { id: "staff_invites", icon: Mail },
  { id: "leave_requests", icon: ClipboardList },
  { id: "provider_invites", icon: Stethoscope },
  { id: "payments", icon: CreditCard },
  { id: "capacity_alerts", icon: AlertTriangle },
];

function FacilityNotificationSettingsPage() {
  const { facilityId } = Route.useParams();

  return (
    <div className="space-y-6">
      <PageHeader
        title={<Trans>Facility notification preferences</Trans>}
        description={
          <Trans>
            Choose how this facility receives alerts for staff invites, leave requests, provider
            invites, payments, and capacity warnings.
          </Trans>
        }
      />
      <NotificationSettingsClient
        scope={`facility:${facilityId}`}
        categories={FACILITY_CATEGORIES}
        hideUserOnlyFeatures
      />
    </div>
  );
}
